export function normalizeReportStatusKey(status) {
  if (!status) return ''
  const sLower = String(status).trim().toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-')

  switch (sLower) {
    case 'in-progress':
    case 'inprogress':
    case 'draft':
      return 'in-progress'
    case 'submitted':
    case 'under-review':
    case 'in-review':
    case 'sent-to-administrator':
    case 'sent-to-reviewer':
      return 'submitted'
    case 'in-smart-review':
    case 'insmartreview':
      return 'in_smart_review'
    case 'approved':
    case 'saved-and-completed':
      return 'approved'
    case 'rejected':
    case 'denied':
    case 'returned':
      return 'rejected'
    default:
      return sLower
  }
}

export function getCaseStatusMeta(status) {
  const s = (status || '').trim().toLowerCase()
  if (s === 'closed') {
    return {
      label: 'Closed',
      variantClass: 'case-status-indicator--closed',
    }
  }
  if (s === 'draft') {
    return {
      label: 'Draft',
      variantClass: 'case-status-indicator--draft',
    }
  }
  return {
    label: status || 'In Progress',
    variantClass: 'case-status-indicator--in-progress',
  }
}

export function getReportStatusDisplay(status, settings) {
  const canonical = normalizeReportStatusKey(status)
  if (!canonical) return ''

  const cs = settings?.caseStatuses
  if (cs) {
    const map = {
      'in-progress': cs.inProgress?.statusText,
      submitted: cs.submitted?.statusText,
      approved: cs.approved?.statusText,
      rejected: cs.rejected?.statusText,
      closed: cs.closed?.statusText,
      canceled: cs.canceled?.statusText,
      filed: cs.filed?.statusText,
      final: cs.final?.statusText,
      invoiced: cs.invoiced?.statusText,
      in_smart_review: 'In Smart Review',
    }
    if (map[canonical]) return map[canonical]
  }

  const defaults = {
    'in-progress': 'In Progress',
    in_smart_review: 'In Smart Review',
    submitted: 'Sent to Administrator',
    approved: 'Saved and Completed',
    filed: 'Filed',
    invoiced: 'Invoiced',
    final: 'Final',
    rejected: 'Denied',
  }

  return (
    defaults[canonical] ||
    String(status)
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
  )
}

export function getReportStatusBadgeMeta(status) {
  const canonical = normalizeReportStatusKey(status)
  switch (canonical) {
    case 'submitted':
      return {
        canonical,
        tier: 'open',
        badgeClass: 'report-status-pill--submitted',
        icon: 'paper-plane',
      }
    case 'in_smart_review':
      return {
        canonical,
        tier: 'open',
        badgeClass: 'report-status-pill--smart-review',
        icon: 'magic',
      }
    case 'in-progress':
      return {
        canonical,
        tier: 'open',
        badgeClass: 'report-status-pill--in-progress',
        icon: 'pen',
      }
    case 'rejected':
      return {
        canonical,
        tier: 'open',
        badgeClass: 'report-status-pill--rejected',
        icon: 'exclamation-circle',
      }
    case 'approved':
      return {
        canonical,
        tier: 'closed',
        badgeClass: 'report-status-pill--approved',
        icon: 'check-circle',
      }
    case 'filed':
    case 'final':
      return {
        canonical,
        tier: 'closed',
        badgeClass: 'report-status-pill--filed',
        icon: 'file-check',
      }
    case 'invoiced':
      return {
        canonical,
        tier: 'closed',
        badgeClass: 'report-status-pill--invoiced',
        icon: 'file-invoice-dollar',
      }
    default:
      return {
        canonical,
        tier: 'closed',
        badgeClass: 'report-status-pill--filed',
        icon: 'file-alt',
      }
  }
}

export function getReportIdentifier(rowData) {
  if (!rowData?.reportStatus) return ''
  const type = rowData.reportType || (rowData.reportNumber <= 1 ? 'Initial' : 'Progress')
  const seq =
    rowData.reportSequenceNumber || (rowData.reportNumber && rowData.reportNumber > 1 ? rowData.reportNumber - 1 : 1)

  if (type === 'Initial') return 'Initial'
  if (type === 'Final') return 'Final'
  if (type === 'CaseManagement') return 'Case Mgmt'
  if (type === 'StatusReport') return seq > 1 ? `Status #${seq}` : 'Status Report'
  return `Progress #${seq}`
}

export function getReportDeepLink(rowData) {
  if (!rowData?.caseNumber) return '/cases'
  if (!rowData.reportStatus || rowData.reportIsExternal) {
    return `/cases/${rowData.caseNumber}/reports`
  }
  const canonical = normalizeReportStatusKey(rowData.reportStatus)
  if ((canonical === 'in-progress' || canonical === 'rejected') && canonical !== 'final') {
    return `/cases/${rowData.caseNumber}/reports/in-progress`
  }
  if (rowData.reportNumber) {
    return `/cases/${rowData.caseNumber}/reports/${rowData.reportNumber}`
  }
  return `/cases/${rowData.caseNumber}/reports`
}
